import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Fragment, forwardRef, useId, useState } from 'react';

import { cn } from '../utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  /**
   * Select label
   */
  label?: string;

  /**
   * Available options
   */
  options: SelectOption[];

  /**
   * Selected value
   */
  value?: string | number;

  /**
   * Change handler
   */
  onChange?: (value: string | number) => void;

  /**
   * Select size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Helper text to display below select
   */
  helperText?: string;

  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;

  /**
   * Enable search/filter functionality
   * @default false
   */
  searchable?: boolean;

  /**
   * Full width select
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom container class name
   */
  containerClassName?: string;

  /**
   * Custom button class name
   */
  buttonClassName?: string;

  /**
   * Custom label class name
   */
  labelClassName?: string;
}

/**
 * Select component
 *
 * A versatile select dropdown with search functionality, validation, and accessibility.
 * Built on Headless UI Listbox for full keyboard navigation support.
 *
 * @example
 * ```tsx
 * <Select
 *   label="Country"
 *   options={countries}
 *   value={selectedCountry}
 *   onChange={setSelectedCountry}
 * />
 *
 * <Select
 *   label="Investment Type"
 *   options={types}
 *   searchable
 *   placeholder="Select a type..."
 * />
 * ```
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      label,
      options,
      value,
      onChange,
      size = 'md',
      error,
      helperText,
      placeholder = 'Select an option',
      searchable = false,
      fullWidth = false,
      required = false,
      disabled = false,
      containerClassName,
      buttonClassName,
      labelClassName,
    },
    ref
  ) => {
    const [query, setQuery] = useState('');

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions =
      query === '' || !searchable
        ? options
        : options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));

    const autoId = useId();
    const buttonId = `select-${autoId}`;
    const errorId = `${buttonId}-error`;
    const helperTextId = `${buttonId}-helper`;

    const baseStyles = [
      'relative w-full rounded-lg border text-left',
      'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-900',
      'disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500 dark:disabled:text-gray-600 disabled:cursor-not-allowed',
    ];

    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
    };

    const stateStyles = error
      ? [
          'border-error-500',
          'text-error-900 dark:text-error-200',
          'focus:ring-error-500',
          'focus:border-error-500',
        ]
      : [
          'border-gray-300 dark:border-gray-600',
          'focus:ring-primary-500',
          'focus:border-primary-500 dark:focus:border-primary-400',
        ];

    return (
      <div className={cn(fullWidth ? 'w-full' : '', containerClassName)}>
        <Listbox value={value} onChange={(newValue) => onChange?.(newValue)} disabled={disabled}>
          {({ open }) => (
            <>
              {label && (
                <Listbox.Label
                  className={cn(
                    'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1',
                    disabled && 'text-gray-500 dark:text-gray-600',
                    labelClassName
                  )}
                >
                  {label}
                  {required && <span className="text-error-500 dark:text-error-400 ml-1">*</span>}
                </Listbox.Label>
              )}

              <div className="relative">
                <Listbox.Button
                  ref={ref}
                  className={cn(baseStyles, sizeStyles[size], stateStyles, buttonClassName)}
                  aria-invalid={error ? 'true' : 'false'}
                  aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
                  aria-required={required}
                >
                  <span
                    className={cn(
                      'block truncate',
                      !selectedOption && 'text-gray-400 dark:text-gray-500'
                    )}
                  >
                    {selectedOption ? selectedOption.label : placeholder}
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>

                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                    {searchable && (
                      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <input
                          type="text"
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:border-primary-400"
                          placeholder="Search..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}

                    {filteredOptions.length === 0 ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                        No options found.
                      </div>
                    ) : (
                      filteredOptions.map((option) => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          className={({ active }) =>
                            cn(
                              'relative cursor-pointer select-none py-2 pl-10 pr-4',
                              active
                                ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-900 dark:text-primary-200'
                                : 'text-gray-900 dark:text-gray-100',
                              option.disabled && 'cursor-not-allowed opacity-50'
                            )
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={cn(
                                  'block truncate',
                                  selected ? 'font-medium' : 'font-normal'
                                )}
                              >
                                {option.label}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600 dark:text-primary-400">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    )}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>

        {error && (
          <p id={errorId} className="mt-1 text-sm text-error-600 dark:text-error-400" role="alert">
            {error}
          </p>
        )}

        {!error && helperText && (
          <p id={helperTextId} className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
